import React, { useState, useEffect, useCallback } from 'react';
import { History, RefreshCw, Search, AlertTriangle, CheckCircle2, XCircle, Link2 } from 'lucide-react';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageOptionsButton from './PageOptionsButton';

/**
 * Email history — every message that has actually gone out.
 *
 * The source is `email_send_log`, one append-only row per recipient per send.
 * It deliberately does NOT read `email_threads`: that table holds conversation
 * state (one row per lead+recipient, rewritten on every send), so it can say
 * which thread to reply into but never what was sent, by whom, or when.
 *
 * Scoping is enforced on the server: an admin sees every account, everyone else
 * sees only their own sends. The BDA / mailbox filters below are therefore only
 * meaningful for an admin, and are hidden for everyone else.
 */

const PAGE_SIZE = 100;

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysAgoLocal = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EmailLog = ({ onViewLead }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [needsMigration, setNeedsMigration] = useState(false);
  const [scope, setScope] = useState('mine');
  const [filterOptions, setFilterOptions] = useState({ bdes: [], senders: [] });
  const [offset, setOffset] = useState(0);

  const [bde, setBde] = useState('All');
  const [sender, setSender] = useState('All');
  const [status, setStatus] = useState('All');
  const [range, setRange] = useState('30');          // days, or 'all'
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  // Typing shouldn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (bde !== 'All') params.bde = bde;
      if (sender !== 'All') params.sender = sender;
      if (status !== 'All') params.status = status;
      if (debounced) params.q = debounced;
      if (range !== 'all') {
        params.from = daysAgoLocal(parseInt(range, 10));
        params.to = todayLocal();
      }
      const res = await dashboardAPI.getEmailLog(params);
      if (res?.needsMigration) {
        setNeedsMigration(true);
        setRows([]);
        setTotal(0);
      } else if (res?.success) {
        setNeedsMigration(false);
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotal(res.total || 0);
        setScope(res.scope || 'mine');
      } else {
        setError(res?.error || 'Could not load the email history.');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not load the email history.');
    } finally {
      setLoading(false);
    }
  }, [bde, sender, status, range, debounced, offset]);

  useEffect(() => { load(); }, [load]);

  // Changing a filter goes back to the first page.
  useEffect(() => { setOffset(0); }, [bde, sender, status, range, debounced]);

  useEffect(() => {
    let cancelled = false;
    dashboardAPI.getEmailLogFilters()
      .then(res => { if (!cancelled && res?.success) setFilterOptions({ bdes: res.bdes || [], senders: res.senders || [] }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isAdminView = scope === 'all';

  const exportCsv = () => {
    const head = ['Sent', 'BDA', 'From', 'To', 'Company', 'Stage', 'Subject', 'Status', 'Threaded'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [head.join(',')].concat(rows.map(r => [
      fmtWhen(r.sent_at), r.sender_name, r.from_address, r.recipient_email,
      r.company_name, r.stage, r.subject, r.status, r.threaded ? 'yes' : 'no'
    ].map(esc).join(','))).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-history-${todayLocal()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sentCount = rows.filter(r => r.status === 'sent').length;
  const failedCount = rows.filter(r => r.status === 'failed').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <History className="w-8 h-8 text-[#679632]" />
          Email History
        </h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {isAdminView ? 'All accounts' : 'Your sends only'}
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 text-sm"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <PageOptionsButton onExportExcel={exportCsv} showExport />
        </div>
      </div>

      {needsMigration && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex gap-3">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">The history table has not been created yet.</div>
            <div className="mt-1">
              Run <code className="px-1 py-0.5 bg-amber-100 rounded">backend/migrations/add-email-send-log.sql</code> against
              the database. Until then nothing is recorded — sending still works, it just is not logged.
            </div>
          </div>
        </div>
      )}

      {error && !needsMigration && (
        <div className="p-3 rounded-lg bg-red-50 text-red-800 text-sm">{error}</div>
      )}

      {/* Filters. The BDA and mailbox pickers only do anything for an admin,
          because the server pins everyone else to their own sends. */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg">
        <div className="relative">
          <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search recipient, company, subject…"
            className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm w-72 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
          />
        </div>

        {isAdminView && (
          <>
            <select
              value={bde}
              onChange={(e) => setBde(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
              title="Filter by the BDA who sent it"
            >
              <option value="All">All BDAs</option>
              {filterOptions.bdes.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
              title="Filter by the mailbox it was sent from"
            >
              <option value="All">All accounts</option>
              {filterOptions.senders.map(sAddr => <option key={sAddr} value={sAddr}>{sAddr}</option>)}
            </select>
          </>
        )}

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
        >
          <option value="All">Sent and failed</option>
          <option value="sent">Sent only</option>
          <option value="failed">Failed only</option>
        </select>

        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="all">All time</option>
        </select>

        <div className="ml-auto flex items-center gap-3 text-sm text-gray-600">
          <span className="inline-flex items-center gap-1"><CheckCircle2 size={14} className="text-green-600" /> {sentCount}</span>
          {failedCount > 0 && <span className="inline-flex items-center gap-1"><XCircle size={14} className="text-red-500" /> {failedCount}</span>}
          <span className="text-gray-400">of {total}</span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-gray-600">
                <th className="px-3 py-2 font-medium whitespace-nowrap">Sent</th>
                {isAdminView && <th className="px-3 py-2 font-medium whitespace-nowrap">BDA</th>}
                <th className="px-3 py-2 font-medium whitespace-nowrap">From</th>
                <th className="px-3 py-2 font-medium whitespace-nowrap">To</th>
                <th className="px-3 py-2 font-medium whitespace-nowrap">Company</th>
                <th className="px-3 py-2 font-medium whitespace-nowrap">Stage</th>
                <th className="px-3 py-2 font-medium">Subject</th>
                <th className="px-3 py-2 font-medium whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtWhen(r.sent_at)}</td>
                  {isAdminView && <td className="px-3 py-2 whitespace-nowrap">{r.sender_name || '—'}</td>}
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                    {r.from_address || '—'}
                    {r.provider === 'graph' && (
                      <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-[#eaf3e0] text-[#4a6f22]">M365</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.recipient_name ? <span className="text-gray-900">{r.recipient_name} </span> : null}
                    <span className="text-gray-600">{r.recipient_email}</span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.lead_id && onViewLead ? (
                      <button type="button" onClick={() => onViewLead(r.lead_id)} className="text-[#679632] hover:underline">
                        {r.company_name || '—'}
                      </button>
                    ) : (r.company_name || '—')}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                    {r.stage || (r.follow_up_number ? `Follow-up ${r.follow_up_number}` : '—')}
                  </td>
                  <td className="px-3 py-2 max-w-md">
                    <div className="truncate" title={r.subject || ''}>{r.subject || '—'}</div>
                    {r.status === 'failed' && r.error && (
                      <div className="text-xs text-red-600 mt-0.5 truncate" title={r.error}>{r.error}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.status === 'sent' ? (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <CheckCircle2 size={14} /> Sent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600">
                        <XCircle size={14} /> Failed
                      </span>
                    )}
                    {r.threaded && (
                      <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded bg-blue-50 text-blue-700" title="Landed in the existing conversation">
                        <Link2 size={10} /> thread
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && rows.length === 0 && !needsMigration && (
          <div className="text-center py-12 text-gray-500">
            Nothing sent in this period. The log starts from the moment the history table was created —
            anything sent before that is not in here.
          </div>
        )}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#679632]" />
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            ← Newer
          </button>
          <span className="text-gray-500">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            Older →
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailLog;
